import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Button,
  Table,
  message,
  Typography,
  Space,
  Alert,
  Tag,
  Card,
  Statistic,
  Row,
  Col,
  Tabs,
  Spin,
  Select,
  Input,
  Badge,
} from "antd";
const { Option } = Select;
import {
  PlusOutlined,
  DisconnectOutlined,
  LinkOutlined,
  DatabaseOutlined,
  CloudOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  PicqerService,
  type PicqerProduct,
  type ProductImportResult,
} from "../services/picqerService";
import { WmsProductService } from "../services/wmsProductService";
import type { WmsProvider } from "../services/wmsOrdersService";
import {
  useCompanyProducts,
  type CompanyProduct,
} from "../hooks/useCompanyProducts";
import { Company, isChannelDock } from "../types/company";

const { Title, Text } = Typography;

interface PicqerProductImportProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  company: Company;
  /** When set, use this WMS for import (e.g. "channeldock" when opened from ChannelDock card). */
  wmsProviderOverride?: "picqer" | "channeldock";
}

interface ImportItem extends PicqerProduct {
  selected: boolean;
  matchType?: "ean" | "productcode" | "name";
  existingProduct?: Record<string, unknown>;
  isNew: boolean;
}

export const PicqerProductImport: React.FC<PicqerProductImportProps> = ({
  visible,
  onCancel,
  company,
  wmsProviderOverride,
}) => {
  const { t } = useTranslation();
  const { products: companyProducts, refresh: refreshCompanyProducts } =
    useCompanyProducts(company.id);
  const [loading, setLoading] = useState(false);
  const [importData, setImportData] = useState<ProductImportResult | null>(
    null
  );
  const [importItems, setImportItems] = useState<ImportItem[]>([]);
  const [importing, setImporting] = useState(false);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("unlinked");

  const wmsProvider: WmsProvider = wmsProviderOverride ?? (isChannelDock(company) ? "channeldock" : "picqer");
  const wmsName = wmsProvider === "channeldock" ? "ChannelDock" : "Picqer";
  const canImport =
    company.id &&
    (wmsProvider === "channeldock"
      ? !!(company.channeldock_enabled && company.channeldock_api_key)
      : !!company.picqer_api_key);

  // Add useEffect to fetch products when modal opens
  useEffect(() => {
    if (visible && canImport) {
      handleFetchProducts();
    }
  }, [visible, canImport, company.id]);

  const handleFetchProducts = async () => {
    if (!company.id) return;
    if (wmsProvider === "picqer" && !company.picqer_api_key) {
      message.error(t("myProducts.picqerImportModal.fetchStep.apiKeyMissing"));
      return;
    }

    setLoading(true);
    try {
      const result = await WmsProductService.importProducts(
        company.id,
        wmsProvider,
        company.picqer_api_key
      );

      if (result.success && result.data) {
        setImportData(result.data);

        // Create import items (new products + matched products for reference)
        const items: ImportItem[] = [
          ...result.data.newProducts.map((product) => ({
            ...product,
            selected: false,
            isNew: true,
          })),
          ...result.data.matchedProducts.map(
            ({ picqerProduct, existingProduct, matchType }) => ({
              ...picqerProduct,
              selected: false,
              matchType,
              existingProduct,
              isNew: false,
            })
          ),
        ];

        setImportItems(items);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      message.error(t("myProducts.picqerImportModal.fetchStep.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  const handleImportSingle = async (wmsProductId: number) => {
    if (wmsProvider === "channeldock") {
      message.info("Use “Link to existing product” to link a ChannelDock product to a catalog product.");
      return;
    }
    if (!company.picqer_api_key || !company.id) {
      message.error(t("myProducts.picqerImportModal.fetchStep.apiKeyMissing"));
      return;
    }

    setImporting(true);
    try {
      const result = await PicqerService.importProduct(
        company.id!,
        wmsProductId
      );

      if (result.success) {
        message.success(
          t(
            "myProducts.picqerImportModal.selectStep.messages.importSuccessSingle"
          )
        );
        await refreshCompanyProducts();
        await handleFetchProducts();
      } else {
        message.error(
          t(
            "myProducts.picqerImportModal.selectStep.messages.importErrorSingle"
          )
        );
      }
    } catch (error) {
      console.error("Error importing product:", error);
      message.error(
        t("myProducts.picqerImportModal.selectStep.messages.importErrorGeneric")
      );
    } finally {
      setImporting(false);
    }
  };

  const handleManualLink = async (
    wmsProductId: number,
    existingProductId: number | null
  ) => {
    if (!existingProductId) {
      message.error(
        t(
          "myProducts.picqerImportModal.selectStep.messages.noExistingProductSelected"
        )
      );
      return;
    }

    setLinking(true);
    try {
      const result = await WmsProductService.linkProduct(
        company.id!,
        wmsProvider,
        wmsProductId,
        existingProductId
      );

      if (result.success) {
        message.success(
          t(
            "myProducts.picqerImportModal.selectStep.messages.manualLinkSuccess"
          )
        );
        await refreshCompanyProducts();
        await handleFetchProducts();
      } else {
        message.error(
          t("myProducts.picqerImportModal.selectStep.messages.manualLinkError")
        );
      }
    } catch (error) {
      console.error("Error linking product:", error);
      message.error(
        t(
          "myProducts.picqerImportModal.selectStep.messages.manualLinkErrorGeneric"
        )
      );
    } finally {
      setLinking(false);
    }
  };

  const handleLinkAllSuggestedMatches = async () => {
    if (!company.id || wmsProvider !== "channeldock") return;
    const items = importItems.filter(
      (item) =>
        item.matchType &&
        item.existingProduct &&
        item.existingProduct.channeldock_product_id == null &&
        typeof (item.existingProduct as { id: number }).id === "number"
    );
    if (items.length === 0) {
      message.info("No suggested matches to link.");
      return;
    }

    setLinking(true);
    try {
      const links = items.map((item) => ({
        wmsProductId: item.idproduct,
        existingProductId: (item.existingProduct as { id: number }).id,
      }));
      const result = await WmsProductService.linkProducts(
        company.id,
        wmsProvider,
        links
      );

      await refreshCompanyProducts();
      await handleFetchProducts();

      if (result.successCount > 0) {
        message.success(
          `Linked ${result.successCount} ChannelDock product${
            result.successCount === 1 ? "" : "s"
          } to suggested catalog matches.`
        );
      }
      if (result.errorCount > 0) {
        const hint =
          result.errors?.length && result.errors.length > 0
            ? ` ${result.errors.slice(0, 3).join("; ")}${
                result.errors.length > 3 ? "…" : ""
              }`
            : "";
        message.warning(`${result.errorCount} link(s) failed.${hint}`);
      }
      if (result.successCount === 0 && result.errorCount === 0 && result.error) {
        message.error(result.error);
      }
    } catch (error) {
      console.error("Error linking suggested matches:", error);
      message.error(
        t(
          "myProducts.picqerImportModal.selectStep.messages.manualLinkErrorGeneric"
        )
      );
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkProduct = async (productId: number) => {
    if (!company.id) return;

    setUnlinking(true);
    try {
      const result = await WmsProductService.unlinkProduct(
        company.id,
        wmsProvider,
        productId
      );

      if (result.success) {
        message.success(
          t("myProducts.picqerImportModal.selectStep.messages.unlinkSuccess")
        );
        await refreshCompanyProducts();
        await handleFetchProducts();
      } else {
        message.error(
          t("myProducts.picqerImportModal.selectStep.messages.unlinkError")
        );
      }
    } catch (error) {
      console.error("Error unlinking product:", error);
      message.error(
        t("myProducts.picqerImportModal.selectStep.messages.unlinkErrorGeneric")
      );
    } finally {
      setUnlinking(false);
    }
  };

  const renderProductsTable = () => {
    const wmsIdField = wmsProvider === "channeldock" ? "channeldock_product_id" : "picqer_product_id";
    const linkedProducts = companyProducts.filter((product) =>
      wmsProvider === "channeldock" ? product.channeldock_product_id : product.picqer_product_id
    );
    const unlinkedCompanyProducts = companyProducts.filter((product) =>
      wmsProvider === "channeldock" ? !product.channeldock_product_id : !product.picqer_product_id
    );

    // Filter import items: only show those not already linked in this WMS
    const filteredUnlinkedProducts = importItems.filter((item) => {
      const existingWmsId = item.existingProduct?.[wmsIdField];
      if (existingWmsId != null) return false;
      const searchLower = searchTerm.toLowerCase();
      return (
        item.name.toLowerCase().includes(searchLower) ||
        (item.ean && item.ean.toString().includes(searchLower)) ||
        (item.barcode && item.barcode.toString().includes(searchLower)) ||
        (item.productcode &&
          item.productcode.toLowerCase().includes(searchLower))
      );
    });

    const suggestedLinkableCount = importItems.filter(
      (item) =>
        item.matchType &&
        item.existingProduct &&
        item.existingProduct[wmsIdField] == null &&
        typeof (item.existingProduct as { id: number }).id === "number"
    ).length;

    return (
      <div>
        {/* Statistics Overview */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={
                  <Space>
                    <CloudOutlined style={{ color: "#1890ff" }} />
                    <Text strong>{wmsName} Products</Text>
                  </Space>
                }
                value={importData?.totalFetched || 0}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={
                  <Space>
                    <DatabaseOutlined style={{ color: "#52c41a" }} />
                    <Text strong>Your Products</Text>
                  </Space>
                }
                value={companyProducts.length}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={
                  <Space>
                    <LinkOutlined style={{ color: "#722ed1" }} />
                    <Text strong>Linked</Text>
                  </Space>
                }
                value={linkedProducts.length}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={
                  <Space>
                    <PlusOutlined style={{ color: "#fa8c16" }} />
                    <Text strong>Available to Import</Text>
                  </Space>
                }
                value={filteredUnlinkedProducts.length}
                valueStyle={{ color: "#fa8c16" }}
              />
            </Card>
          </Col>
        </Row>

        <Tabs
          defaultActiveKey="unlinked"
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
        >
          <Tabs.TabPane
            tab={
              <Badge count={filteredUnlinkedProducts.length} size="small">
                <span>
                  <CloudOutlined style={{ marginRight: 4 }} />
                  {wmsName} Products
                </span>
              </Badge>
            }
            key="unlinked"
          >
            {filteredUnlinkedProducts.length > 0 ? (
              <div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <Input.Search
                    placeholder={`Search ${wmsName} products...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ flex: "1 1 280px", minWidth: 200, maxWidth: 480 }}
                    allowClear
                    prefix={<CloudOutlined />}
                  />
                  {wmsProvider === "channeldock" && suggestedLinkableCount > 0 ? (
                    <Button
                      type="primary"
                      icon={<LinkOutlined />}
                      onClick={() => void handleLinkAllSuggestedMatches()}
                      loading={linking}
                    >
                      Link all suggested matches ({suggestedLinkableCount})
                    </Button>
                  ) : null}
                </div>

                <Table
                  columns={[
                    {
                      title: (
                        <Space>
                          <CloudOutlined />
                          <span>{wmsName} Product</span>
                        </Space>
                      ),
                      dataIndex: "name",
                      key: "name",
                      render: (name: string, record: ImportItem) => (
                        <Card size="small" style={{ borderColor: "#1890ff" }}>
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <Text strong style={{ color: "#1890ff" }}>
                              {name}
                            </Text>
                            {record.productcode && (
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                Code: {record.productcode}
                              </Text>
                            )}
                            {record.ean && (
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                EAN: {record.ean}
                              </Text>
                            )}
                            {record.matchType && record.existingProduct && (
                              <Tag color="blue" icon={<LinkOutlined />}>
                                Suggested match:{" "}
                                {
                                  (record.existingProduct as { name: string })
                                    .name
                                }
                              </Tag>
                            )}
                          </Space>
                        </Card>
                      ),
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      width: 300,
                      render: (_: unknown, record: ImportItem) => (
                        <Space
                          direction="vertical"
                          size="small"
                          style={{ width: "100%" }}
                        >
                          {/* If there's a suggested match, show link button */}
                          {record.matchType && record.existingProduct && (
                            <Button
                              type="default"
                              size="small"
                              icon={<LinkOutlined />}
                              onClick={() =>
                                handleManualLink(
                                  record.idproduct,
                                  (record.existingProduct as { id: number }).id
                                )
                              }
                              loading={linking}
                              style={{ width: "100%" }}
                            >
                              Link to Suggested Match
                            </Button>
                          )}

                          {/* Link to existing product - always available */}
                          <Select
                            showSearch
                            allowClear
                            size="small"
                            style={{ width: "100%" }}
                            placeholder="Link to existing product..."
                            optionFilterProp="children"
                            onChange={(value: number | null) =>
                              handleManualLink(record.idproduct, value)
                            }
                            value={record.existingProduct?.id as number | null}
                            loading={linking}
                            filterOption={(input, option) => {
                              const optionText =
                                option?.children?.toString() || "";
                              return optionText
                                .toLowerCase()
                                .includes(input.toLowerCase());
                            }}
                          >
                            {unlinkedCompanyProducts.map((p) => (
                              <Option key={p.id} value={p.id}>
                                {p.name}{" "}
                                {p.ean ? `(${p.ean})` : ""}
                              </Option>
                            ))}
                          </Select>

                          {/* Import as new product - only for Picqer; ChannelDock uses link only */}
                          {wmsProvider === "picqer" && !record.existingProduct?.id && (
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() =>
                                handleImportSingle(record.idproduct)
                              }
                              loading={importing}
                              style={{ width: "100%" }}
                            >
                              Import as New Product
                            </Button>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                  dataSource={filteredUnlinkedProducts}
                  rowKey="idproduct"
                  pagination={{ pageSize: 10 }}
                  size="small"
                  scroll={{ x: 800 }}
                />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <CloudOutlined style={{ fontSize: "48px", color: "#d9d9d9" }} />
                <Title level={4} style={{ marginTop: 16, color: "#666" }}>
                  No {wmsName} Products Available
                </Title>
                <Text type="secondary">
                  All {wmsName} products have been imported or linked to your
                  products.
                </Text>
              </div>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <Badge count={linkedProducts.length} size="small">
                <span>
                  <LinkOutlined style={{ marginRight: 4 }} />
                  Linked Products
                </span>
              </Badge>
            }
            key="linked"
          >
            {linkedProducts.length > 0 ? (
              <div>
                <Alert
                  message="Linked Products"
                  description={`${linkedProducts.length} products are currently linked between ${wmsName} and your catalog.`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Table
                  columns={[
                    {
                      title: (
                        <Space>
                          <DatabaseOutlined />
                          <span>Your Product</span>
                        </Space>
                      ),
                      key: "name",
                      render: (_: unknown, record: CompanyProduct) => (
                        <Card size="small" style={{ borderColor: "#52c41a" }}>
                          <Space direction="vertical" size="small">
                            <Text strong style={{ color: "#52c41a" }}>
                              {record.name}
                            </Text>
                            {record.ean && (
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                EAN: {record.ean}
                              </Text>
                            )}
                          </Space>
                        </Card>
                      ),
                    },
                    {
                      title: (
                        <Space>
                          <CloudOutlined />
                          <span>{wmsName} ID</span>
                        </Space>
                      ),
                      dataIndex: wmsProvider === "channeldock" ? "channeldock_product_id" : "picqer_product_id",
                      key: "wms_id",
                      render: (id: number) => (
                        <Tag color="blue" icon={<CloudOutlined />}>
                          #{id}
                        </Tag>
                      ),
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      width: 200,
                      render: (_: unknown, record: CompanyProduct) => {
                        const openUrl =
                          wmsProvider === "channeldock"
                            ? `https://channeldock.com/portal/public/centersellers`
                            : `https://e-freight-forwarding.picqer.com/products/${record.picqer_product_id}`;
                        return (
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <Button
                              type="link"
                              size="small"
                              icon={<CloudOutlined />}
                              onClick={() => window.open(openUrl, "_blank")}
                            >
                              Open in {wmsName}
                            </Button>
                            <Button
                              type="link"
                              danger
                              size="small"
                              icon={<DisconnectOutlined />}
                              onClick={() => {
                                Modal.confirm({
                                  title: "Unlink Product",
                                  content: `Are you sure you want to unlink "${record.name}" from ${wmsName}?`,
                                  okText: "Unlink",
                                  cancelText: "Cancel",
                                  okButtonProps: { danger: true },
                                  onOk: () => handleUnlinkProduct(record.id),
                                });
                              }}
                              loading={unlinking}
                            >
                              Unlink
                            </Button>
                          </Space>
                        );
                      },
                    },
                  ]}
                  dataSource={linkedProducts}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="small"
                  scroll={{ x: 600 }}
                />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <LinkOutlined style={{ fontSize: "48px", color: "#d9d9d9" }} />
                <Title level={4} style={{ marginTop: 16, color: "#666" }}>
                  No Linked Products
                </Title>
                <Text type="secondary">
                  No products are currently linked between {wmsName} and your
                  catalog.
                </Text>
              </div>
            )}
          </Tabs.TabPane>
        </Tabs>
      </div>
    );
  };

  const renderStep = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Fetching products from {wmsName}...</Text>
          </div>
        </div>
      );
    }

    if (importData) {
      return <>{renderProductsTable()}</>;
    }

    return (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <Space direction="vertical" size="large">
          <CloudOutlined style={{ fontSize: "48px", color: "#1890ff" }} />
          <Title level={3}>Import Products from {wmsName}</Title>
          <Text type="secondary">
            Click the button below to fetch products from your {wmsName} account
            and see which ones can be imported or linked.
          </Text>
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleFetchProducts}
            icon={<SyncOutlined />}
          >
            {loading ? "Fetching Products..." : `Fetch Products from ${wmsName}`}
          </Button>
        </Space>
      </div>
    );
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <CloudOutlined />
          <span>
            {wmsProvider === "channeldock"
              ? "Import Products from ChannelDock"
              : "Import Products from Picqer"}
          </span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1400}
      maskClosable={false}
    >
      {renderStep()}
    </Modal>
  );
};
